import React from 'react';

import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

import VoucherPublicPreviewCard from '~/components/Vouchers/VoucherPublicPreviewCard';
import VoucherTextReplacements from '~/components/Vouchers/VoucherTextReplacements';

import type { PreviewPublicVoucher, PreviewVoucherTextReplacement } from '~/services/PromoService';

type Props = {
  localError: string;
  isError: boolean;
  errorHeadline: string;
  errorDetailBlock: string;
  publicVoucher?: PreviewPublicVoucher;
  textReplacements?: Array<PreviewVoucherTextReplacement>;
  isPending?: boolean;
  emptyMessage: string;
  loadingMessage?: string;
  localeLabel?: string;
  untranslatedFields?: Array<string>;
  showFeedback?: boolean;
  showPreviewCard?: boolean;
  showTextReplacements?: boolean;
  onPreviewDetailsClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
};

type VoucherTranslationSource = {
  voucherAmountDisplay?: string;
  voucherDescription?: string;
  voucherTitle?: string;
};

function hasSameText(source?: string, translated?: string | null): boolean {
  return Boolean(source?.trim()) && source.trim() === translated?.trim();
}

/**
 * This is intentionally a heuristic: identical source and translated strings are
 * likely a missing overlay, but can also be valid proper names or product copy.
 */
export function getUntranslatedVoucherPreviewFields(
  source: VoucherTranslationSource,
  publicVoucher?: PreviewPublicVoucher,
): Array<string> {
  if (!publicVoucher) {
    return [];
  }

  return [
    hasSameText(source.voucherTitle, publicVoucher.voucherTitle) ? 'Title' : '',
    hasSameText(source.voucherDescription, publicVoucher.voucherDescription) ? 'Description' : '',
    hasSameText(source.voucherAmountDisplay, publicVoucher.voucherAmountDisplay) ? 'Amount display' : '',
  ].filter(Boolean);
}

export default function VoucherPreviewContent({
  localError,
  isError,
  errorHeadline,
  errorDetailBlock,
  publicVoucher,
  textReplacements = [],
  isPending = false,
  emptyMessage,
  loadingMessage,
  localeLabel,
  untranslatedFields = [],
  showFeedback = true,
  showPreviewCard = true,
  showTextReplacements = true,
  onPreviewDetailsClick,
}: Props) {
  return (
    <Stack spacing={2}>
      {showFeedback && localError ? <Alert severity="warning">{localError}</Alert> : null}
      {showFeedback && isError ? (
        <Alert severity="error">
          <Typography variant="body2">{errorHeadline}</Typography>
          {errorDetailBlock ? (
            <Box component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', m: 0, mt: 1 }}>
              {errorDetailBlock}
            </Box>
          ) : null}
        </Alert>
      ) : null}
      {showFeedback && untranslatedFields.length > 0 ? (
        <Alert severity="error">
          Translation may not have applied to {untranslatedFields.join(', ')} for {localeLabel ?? 'the selected locale'}
          . Check the translated voucher configuration before publishing.
        </Alert>
      ) : null}
      {showPreviewCard ? (
        <Box sx={{ position: 'relative', minHeight: publicVoucher ? undefined : 72 }}>
          {publicVoucher ? (
            <VoucherPublicPreviewCard voucher={publicVoucher} onDetailsClick={onPreviewDetailsClick} />
          ) : null}
          {!publicVoucher && !localError && !isError && !isPending ? (
            <Typography variant="body2" color="text.secondary">
              {emptyMessage}
            </Typography>
          ) : null}
          {isPending && loadingMessage ? (
            <Box
              sx={(theme) => ({
                position: publicVoucher ? 'absolute' : 'relative',
                inset: publicVoucher ? 0 : undefined,
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: publicVoucher ? undefined : 72,
                borderRadius: publicVoucher ? 7 : 1,
                bgcolor: alpha(theme.palette.background.paper, 0.72),
                backdropFilter: publicVoucher ? 'blur(1px)' : undefined,
              })}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  {loadingMessage}
                </Typography>
              </Stack>
            </Box>
          ) : null}
        </Box>
      ) : null}
      {showTextReplacements ? (
        <VoucherTextReplacements
          textReplacements={textReplacements}
          tableAriaLabel="Voucher preview text replacements"
        />
      ) : null}
    </Stack>
  );
}
